import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Activity, Users, Clock, TrendingUp, Package, AlertTriangle, ChevronLeft, ChevronRight, Pause, Play, Eye, EyeOff, X, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SAMIDEL_LOGO = "https://customer-assets.emergentagent.com/job_fabriflow/artifacts/vi8gu83e_logo-hero-samidel.png";

// Configuração do slideshow
const SLIDE_DURATION = 10000; // 10 segundos por slide
const SCROLL_STEP = 400;

export default function TVDashboard() {
  const navigate = useNavigate();
  const [metricas, setMetricas] = useState(null);
  const [ofsAtivas, setOfsAtivas] = useState([]);
  const [operadoresAtivos, setOperadoresAtivos] = useState([]);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showAnomalias, setShowAnomalias] = useState(false); // Ocultar anomalias por defeito
  const [mostrarCores, setMostrarCores] = useState(true); // Toggle cores de produtividade
  const [modoTestes, setModoTestes] = useState(false); // Modo testes - esconder tempos/eficiências
  
  const containerRef = useRef(null);
  const ofsContainerRef = useRef(null);
  
  // Slides sem anomalias por defeito
  const slides = showAnomalias 
    ? ['metricas', 'ofs', 'operadores', 'anomalias']
    : ['metricas', 'ofs', 'operadores'];

  useEffect(() => {
    loadDashboard();
    loadConfiguracao();
    const interval = setInterval(() => {
      loadDashboard();
      setCurrentTime(new Date());
    }, 10000);
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfiguracao = async () => {
    try {
      const response = await axios.get(`${API}/configuracao`);
      setModoTestes(response.data.modo_testes_esconder_tempos || false);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  useEffect(() => {
    if (!autoScroll) return;
    
    const slideInterval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, SLIDE_DURATION);
    
    return () => clearInterval(slideInterval);
  }, [autoScroll, slides.length]);

  useEffect(() => {
    if (!autoScroll || currentSlide !== 1 || !ofsContainerRef.current) return;
    
    const container = ofsContainerRef.current;
    let scrollPos = 0;
    const maxScroll = container.scrollHeight - container.clientHeight;
    
    const scrollInterval = setInterval(() => {
      scrollPos += SCROLL_STEP;
      if (scrollPos > maxScroll) {
        scrollPos = 0;
      }
      container.scrollTo({ top: scrollPos, behavior: 'smooth' });
    }, 3000);
    
    return () => clearInterval(scrollInterval);
  }, [autoScroll, currentSlide]);

  const loadDashboard = async () => {
    try {
      const metricasRes = await axios.get(`${API}/dashboard/metricas`);
      setMetricas(metricasRes.data);

      // Usar novo endpoint otimizado para TV
      try {
        const tvDashboardRes = await axios.get(`${API}/analytics/tv-dashboard`);
        setOperadoresAtivos(tvDashboardRes.data.operadores);
      } catch {
        // Fallback para o método antigo
        const operadoresRes = await axios.get(`${API}/operadores`);
        const operadoresComDesempenho = await Promise.all(
          operadoresRes.data.filter(o => o.perfil === 'operador').map(async (operador) => {
            try {
              const analyticsRes = await axios.get(`${API}/analytics/operador/${operador.id}?periodo=dia`);
              return { ...operador, desempenho: analyticsRes.data };
            } catch {
              return { ...operador, desempenho: { eficiencia_media_percentual: 0, total_quantidade_produzida: 0, total_tempo_produtivo_horas: 0 } };
            }
          })
        );
        setOperadoresAtivos(operadoresComDesempenho);
      }

      const ofsRes = await axios.get(`${API}/ofs`);
      const ofsEmProducao = ofsRes.data.filter(of => ['em_curso', 'pausado'].includes(of.status));
      
      const ofsComProgresso = await Promise.all(
        ofsEmProducao.slice(0, 20).map(async (of) => {
          try {
            const progressoRes = await axios.get(`${API}/ofs/${of.id}/progresso-detalhado`);
            return { ...of, progresso: progressoRes.data };
          } catch {
            return of;
          }
        })
      );
      setOfsAtivas(ofsComProgresso);

      if (showAnomalias) {
        try {
          const ocorrenciasRes = await axios.get(`${API}/ocorrencias`);
          setOcorrencias(ocorrenciasRes.data.filter(o => o.status === 'aberta' || o.status === 'em_analise'));
        } catch {
          setOcorrencias([]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  };

  const getEficienciaColor = (eficiencia) => {
    // Se cores desativadas, retornar cinza
    if (!mostrarCores) {
      return 'text-stone-600 bg-stone-200';
    }
    if (eficiencia >= 100) return 'text-emerald-600 bg-emerald-100';
    if (eficiencia >= 80) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-emerald-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-white text-stone-900">
      {/* Header - Fundo branco com contraste */}
      <header className="bg-white border-b-4 border-blue-600 px-8 py-4 sticky top-0 z-50 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img 
              src={SAMIDEL_LOGO}
              alt="Samidel"
              className="h-12 brightness-0"
            />
            <div>
              <h1 className="text-3xl font-bold text-blue-800">FabriFlow TV</h1>
              <p className="text-stone-500 text-lg">Dashboard em Tempo Real</p>
            </div>
          </div>
          
          {/* Navegação de Slides */}
          <div className="flex items-center gap-6">
            <div className="flex gap-3">
              {slides.map((slide, idx) => (
                <button
                  key={idx}
                  onClick={() => { setCurrentSlide(idx); setAutoScroll(false); }}
                  className={`w-4 h-4 rounded-full transition-all border-2 ${
                    currentSlide === idx 
                      ? 'bg-blue-600 border-blue-600 scale-125' 
                      : 'bg-white border-stone-300 hover:border-blue-400'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={() => setAutoScroll(!autoScroll)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-lg font-semibold transition-all ${
                autoScroll 
                  ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300' 
                  : 'bg-stone-100 text-stone-600 border-2 border-stone-300'
              }`}
            >
              {autoScroll ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
              {autoScroll ? 'AUTO' : 'PAUSA'}
            </button>
            
            {/* Toggle Cores de Produtividade */}
            <button
              onClick={() => setMostrarCores(!mostrarCores)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-lg font-semibold transition-all ${
                mostrarCores 
                  ? 'bg-gradient-to-r from-blue-100 to-green-100 text-blue-700 border-2 border-blue-300' 
                  : 'bg-stone-100 text-stone-600 border-2 border-stone-300'
              }`}
              title={mostrarCores ? 'Ocultar cores de produtividade' : 'Mostrar cores de produtividade'}
            >
              {mostrarCores ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
              CORES
            </button>
          </div>
          
          {/* Relógio grande e visível */}
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-5xl font-bold font-mono text-blue-800">
                {currentTime.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
              <p className="text-xl text-stone-500 capitalize">
                {currentTime.toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            </div>
            
            {/* Botão Fechar */}
            <button
              onClick={() => navigate('/gestor/dashboard')}
              className="p-3 rounded-full bg-stone-100 hover:bg-red-100 text-stone-500 hover:text-red-600 transition-all border-2 border-stone-200 hover:border-red-300"
              title="Fechar TV Dashboard"
            >
              <X className="h-8 w-8" />
            </button>
          </div>
        </div>
      </header>

      {/* Conteúdo Principal */}
      <main className="p-8">
        {/* Slide 0: Métricas Principais */}
        {currentSlide === 0 && metricas && (
          <div className="animate-fadeIn">
            <h2 className="text-4xl font-bold mb-10 text-center text-stone-800">
              📊 Métricas Gerais
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-4 border-blue-300 rounded-3xl p-8 text-center shadow-lg">
                <Package className="h-16 w-16 mx-auto mb-4 text-blue-600" />
                <p className="text-7xl font-bold text-blue-700 mb-2">{metricas.total_ofs}</p>
                <p className="text-2xl font-semibold text-blue-600">OFs Total</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-4 border-emerald-300 rounded-3xl p-8 text-center shadow-lg">
                <TrendingUp className="h-16 w-16 mx-auto mb-4 text-emerald-600" />
                <p className="text-7xl font-bold text-emerald-700 mb-2">{metricas.ofs_andamento || metricas.ofs_em_andamento || 0}</p>
                <p className="text-2xl font-semibold text-emerald-600">Em Produção</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-4 border-purple-300 rounded-3xl p-8 text-center shadow-lg">
                <Users className="h-16 w-16 mx-auto mb-4 text-purple-600" />
                <p className="text-7xl font-bold text-purple-700 mb-2">{metricas.total_operadores}</p>
                <p className="text-2xl font-semibold text-purple-600">Operadores</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-4 border-amber-300 rounded-3xl p-8 text-center shadow-lg">
                <Clock className="h-16 w-16 mx-auto mb-4 text-amber-600" />
                <p className="text-7xl font-bold text-amber-700 mb-2">{metricas.operadores_ativos || 0}</p>
                <p className="text-2xl font-semibold text-amber-600">Ativos Agora</p>
              </div>
            </div>
            
            {/* Anomalias em destaque */}
            {ocorrencias.length > 0 && (
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border-4 border-red-300 rounded-3xl p-8 text-center shadow-lg">
                <AlertTriangle className="h-14 w-14 mx-auto mb-3 text-red-600" />
                <p className="text-6xl font-bold text-red-600">{ocorrencias.length}</p>
                <p className="text-2xl font-semibold text-red-500">Anomalias Pendentes</p>
              </div>
            )}
          </div>
        )}

        {/* Slide 1: OFs em Produção */}
        {currentSlide === 1 && (
          <div className="animate-fadeIn">
            <h2 className="text-4xl font-bold mb-8 flex items-center gap-4 text-stone-800">
              <Package className="h-10 w-10 text-blue-600" />
              Ordens em Produção ({ofsAtivas.length})
            </h2>
            <div ref={ofsContainerRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-h-[calc(100vh-280px)] overflow-y-auto scrollbar-hide">
              {ofsAtivas.length === 0 ? (
                <div className="col-span-2 bg-stone-100 border-4 border-stone-300 rounded-3xl p-16 text-center">
                  <p className="text-3xl text-stone-500">Nenhuma OF em produção</p>
                </div>
              ) : (
                ofsAtivas.slice(0, 5).map((of) => {
                  const statusColor = of.status === 'pausado' ? 'border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50' : 'border-emerald-400 bg-gradient-to-br from-emerald-50 to-green-50';
                  const statusLabel = of.status === 'pausado' ? '⏸ PAUSADO' : '🟢 EM PRODUÇÃO';
                  const statusBadgeColor = of.status === 'pausado' ? 'bg-orange-500' : 'bg-emerald-500';
                  const progresso = of.progresso_real || of.progresso?.resumo?.progresso_por_quantidade || 0;
                  
                  // Extrair operadores únicos que trabalharam nesta OF
                  const operadoresUnicos = [];
                  const operadoresMap = new Map();
                  
                  if (of.progresso?.operacoes) {
                    of.progresso.operacoes.forEach(op => {
                      (op.operadores_producao || []).forEach(opr => {
                        if (!operadoresMap.has(opr.operador_id)) {
                          operadoresMap.set(opr.operador_id, {
                            id: opr.operador_id,
                            nome: opr.nome,
                            avatar: opr.avatar,
                            quantidade: opr.quantidade,
                            ativo: of.progresso?.operacoes?.some(o => 
                              o.status === 'em_andamento' && 
                              o.operadores_producao?.some(p => p.operador_id === opr.operador_id)
                            )
                          });
                        } else {
                          const existing = operadoresMap.get(opr.operador_id);
                          existing.quantidade += opr.quantidade;
                        }
                      });
                    });
                  }
                  
                  operadoresMap.forEach(v => operadoresUnicos.push(v));
                  
                  return (
                    <div key={of.id} className={`border-4 ${statusColor} rounded-3xl p-6 shadow-lg hover:shadow-xl transition-all relative overflow-hidden`}>
                      {/* Header da OF */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-3xl font-bold text-stone-800">{of.numero_of}</span>
                            <span className={`text-sm px-3 py-1 rounded-full text-white font-semibold ${statusBadgeColor}`}>
                              {statusLabel}
                            </span>
                          </div>
                          <p className="text-xl text-stone-700 font-medium">{of.artigo || 'Sem artigo'}</p>
                          <p className="text-lg text-stone-500">{of.cliente}</p>
                        </div>
                        <div className="text-right">
                          <div className="relative w-24 h-24">
                            <svg className="w-24 h-24 -rotate-90">
                              <circle cx="48" cy="48" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                              <circle 
                                cx="48" cy="48" r="40" 
                                fill="none" 
                                stroke={progresso >= 80 ? '#10b981' : progresso >= 50 ? '#3b82f6' : '#f59e0b'} 
                                strokeWidth="8"
                                strokeDasharray={`${progresso * 2.51} 251`}
                                strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-2xl font-bold text-stone-800">{progresso}%</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Barra de progresso */}
                      <div className="h-3 bg-stone-200 rounded-full overflow-hidden mb-4">
                        <div 
                          className={`h-full transition-all duration-1000 ${getProgressColor(progresso)}`}
                          style={{ width: `${Math.min(progresso, 100)}%` }}
                        />
                      </div>

                      {/* Métricas rápidas */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-white/70 backdrop-blur rounded-xl p-3 text-center">
                          <p className="text-xs text-stone-500 uppercase">Quantidade</p>
                          <p className="text-xl font-bold text-stone-800">{of.quantidade_total || 0}</p>
                        </div>
                        <div className="bg-white/70 backdrop-blur rounded-xl p-3 text-center">
                          <p className="text-xs text-stone-500 uppercase">T. Estimado</p>
                          <p className="text-xl font-bold text-blue-600">{of.progresso?.resumo?.tempo_total_estimado_horas || 0}h</p>
                        </div>
                        <div className="bg-white/70 backdrop-blur rounded-xl p-3 text-center">
                          <p className="text-xs text-stone-500 uppercase">T. Gasto</p>
                          <p className="text-xl font-bold text-amber-600">{of.progresso?.resumo?.tempo_total_gasto_horas || 0}h</p>
                        </div>
                      </div>

                      {/* Operadores que trabalharam nesta OF */}
                      {operadoresUnicos.length > 0 && (
                        <div className="bg-white/80 backdrop-blur rounded-xl p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="h-4 w-4 text-purple-600" />
                            <p className="text-xs text-stone-500 uppercase font-semibold">Operadores ({operadoresUnicos.length})</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {operadoresUnicos.slice(0, 6).map((opr, idx) => (
                              <div 
                                key={opr.id || idx} 
                                className={`flex items-center gap-2 px-2 py-1 rounded-full text-sm ${
                                  opr.ativo 
                                    ? 'bg-green-100 text-green-700 border border-green-300' 
                                    : 'bg-stone-100 text-stone-600'
                                }`}
                              >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  opr.ativo ? 'bg-green-500 text-white' : 'bg-stone-300 text-stone-600'
                                }`}>
                                  {opr.nome?.charAt(0) || '?'}
                                </div>
                                <span className="font-medium">{opr.nome?.split(' ')[0]}</span>
                                {opr.ativo && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
                              </div>
                            ))}
                            {operadoresUnicos.length > 6 && (
                              <div className="px-2 py-1 rounded-full bg-stone-200 text-stone-600 text-sm">
                                +{operadoresUnicos.length - 6}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Slide 2: Desempenho dos Operadores */}
        {currentSlide === 2 && (
          <div className="animate-fadeIn">
            <h2 className="text-4xl font-bold mb-8 flex items-center gap-4 text-stone-800">
              <Users className="h-10 w-10 text-purple-600" />
              Desempenho dos Operadores (Hoje)
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-h-[calc(100vh-280px)] overflow-y-auto">
              {operadoresAtivos
                .filter(op => op.pecas_hoje > 0 || op.status === 'produzindo')
                .map((operador, index) => {
                  const eficiencia = operador.eficiencia || Math.round(operador.desempenho?.eficiencia_media_percentual || 0);
                  const efColor = getEficienciaColor(eficiencia);
                  const isTop3 = !modoTestes && index < 3 && eficiencia > 0; // Esconder top 3 se modo testes
                  const statusColor = operador.status === 'produzindo' ? 'bg-green-500' : 
                                     operador.status === 'pausado' ? 'bg-amber-500' : 'bg-stone-400';
                  
                  return (
                    <div key={operador.id} className={`bg-white border-4 ${isTop3 ? 'border-amber-400' : 'border-stone-200'} rounded-2xl p-5 shadow-md relative overflow-hidden`}>
                      {/* Status indicator */}
                      <div className={`absolute top-0 right-0 w-3 h-3 rounded-bl-lg ${statusColor}`} />
                      
                      {isTop3 && (
                        <div className="text-center mb-2">
                          <span className="text-3xl">{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-3xl font-bold text-white">{operador.nome_completo?.charAt(0) || '?'}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xl font-bold text-stone-800 truncate">{operador.nome_completo}</p>
                          <p className="text-lg text-stone-500 capitalize">{operador.categoria}</p>
                        </div>
                      </div>
                      
                      {/* Última operação */}
                      {operador.ultima_operacao && (
                        <div className="bg-stone-50 rounded-xl p-3 mb-4">
                          <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Última operação</p>
                          <p className="text-sm font-semibold text-stone-800 truncate">{operador.ultima_operacao.nome}</p>
                          <p className="text-xs text-stone-600">{operador.ultima_operacao.of_numero} • {operador.ultima_operacao.pecas} pç</p>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-4xl font-bold text-stone-800">{operador.pecas_hoje || operador.desempenho?.total_quantidade_produzida || 0}</p>
                          <p className="text-lg text-stone-500">peças</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg text-stone-500">{operador.tempo_produtivo_min ? `${Math.floor(operador.tempo_produtivo_min / 60)}h${operador.tempo_produtivo_min % 60}m` : `${operador.desempenho?.total_tempo_produtivo_horas || 0}h`}</p>
                        </div>
                        {/* Eficiência - ESCONDER se modo testes */}
                        {!modoTestes && (
                          <div className={`px-4 py-2 rounded-xl text-2xl font-bold ${efColor}`}>
                            {eficiencia}%
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Slide 3: Anomalias (apenas se showAnomalias está ativo) */}
        {showAnomalias && currentSlide === 3 && (
          <div className="animate-fadeIn">
            <h2 className="text-4xl font-bold mb-8 flex items-center gap-4 text-stone-800">
              <AlertTriangle className="h-10 w-10 text-red-600" />
              Anomalias Pendentes ({ocorrencias.length})
            </h2>
            {ocorrencias.length === 0 ? (
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-4 border-emerald-300 rounded-3xl p-16 text-center shadow-lg">
                <div className="text-8xl mb-6">✓</div>
                <p className="text-4xl font-bold text-emerald-700">Nenhuma anomalia pendente</p>
                <p className="text-2xl text-emerald-600 mt-4">Todas as ocorrências foram resolvidas</p>
              </div>
            ) : (
              <div className="space-y-6 max-h-[calc(100vh-280px)] overflow-y-auto">
                {ocorrencias.map((oc) => (
                  <div key={oc.id} className="bg-white border-l-8 border-red-500 border-4 border-stone-200 rounded-2xl p-6 shadow-md">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <span className={`px-4 py-2 rounded-full text-lg font-bold text-white ${
                            oc.status === 'aberta' ? 'bg-red-500' : 'bg-amber-500'
                          }`}>
                            {oc.status === 'aberta' ? '🔴 ABERTA' : '🟡 EM ANÁLISE'}
                          </span>
                          <span className="text-xl text-stone-500">
                            {new Date(oc.criado_em).toLocaleString('pt-PT')}
                          </span>
                        </div>
                        <p className="text-2xl text-stone-800">{oc.descricao}</p>
                      </div>
                      {oc.quantidade_afetada > 0 && (
                        <div className="text-right bg-red-50 border-2 border-red-200 rounded-xl p-4">
                          <p className="text-5xl font-bold text-red-600">{oc.quantidade_afetada}</p>
                          <p className="text-lg text-red-500">unidades afetadas</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Navegação inferior */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 flex gap-4 bg-white px-6 py-3 rounded-full border-4 border-blue-300 shadow-xl">
        <button
          onClick={() => { setCurrentSlide(prev => prev > 0 ? prev - 1 : slides.length - 1); setAutoScroll(false); }}
          className="p-3 hover:bg-blue-100 rounded-full transition-colors"
        >
          <ChevronLeft className="h-8 w-8 text-blue-600" />
        </button>
        <span className="px-6 py-3 text-2xl font-bold text-blue-800">
          {currentSlide + 1} / {slides.length}
        </span>
        <button
          onClick={() => { setCurrentSlide(prev => (prev + 1) % slides.length); setAutoScroll(false); }}
          className="p-3 hover:bg-blue-100 rounded-full transition-colors"
        >
          <ChevronRight className="h-8 w-8 text-blue-600" />
        </button>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
